import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { RefreshCw } from 'lucide-react';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, Legend } from 'recharts';
const InverterEfficiencyHeatmap = ({
  startDate,
  endDate
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [inverters, setInverters] = useState([]);
  const [dataPoints, setDataPoints] = useState([]);
  const chartRef = useRef(null);
  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getForEnergyLoss({
        start_date: `${startDate} 00:00:00`,
        end_date: `${endDate} 23:55:00`,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.PLANT_ID]
      });
      const arr = Array.isArray(response.data) ? response.data : (response.data?.body || []);
      let eya = arr?.[0]?.readings?.[0]?.EYA || {};
      if (!arr.length && response.data && response.data.EYA) {
        eya = response.data.EYA;
      }

      // Collect all inverter IDs across different maps to ensure complete coverage
      const lossEff = eya?.inverter_efficiency_loss_inverter_wise?.[0] || {};
      const breakdown = eya?.inverter_breakdown_loss_list_kwh || eya?.inverter_breakdown_kwh_inverter_wise || {};
      const clipping = eya?.inverter_clipping_loss_list_percentage || eya?.inverter_clipping_percentage_inverter_wise || {};

      // Only consider inverter IDs – exclude weather-station IDs present in soiling maps
      const inverterIds = Array.from(new Set([
        ...Object.keys(lossEff || {}),
        ...Object.keys(breakdown || {}),
        ...Object.keys(clipping || {})
      ])).sort();

      const points = inverterIds.map((invId, idx) => {
        const efficiency = typeof lossEff[invId] === 'number' ? Number(lossEff[invId]) : null;
        return {
          x: idx,
          y: 0,
          value: efficiency
        };
      });

      // Remove entries without efficiency value
      const filtered = points.filter(pt => pt.value !== null && !isNaN(pt.value));
      const updatedPoints = filtered.map((p, idx) => ({ ...p, x: idx }));
      const updatedIds = updatedPoints.map(p => inverterIds[p.x]);

      setInverters(updatedIds);
      setDataPoints(updatedPoints);

    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // No chart instance to manage for Recharts
  useEffect(() => {/* noop */}, [inverters, dataPoints, isLoading, startDate]);
  const chartData = inverters.map((id, idx) => ({
    inverter: id,
    efficiency: dataPoints[idx]?.value ?? null
  }));

  const maxEff = Math.ceil(Math.max(...chartData.map(d => d.efficiency || 0), 0));
  const colorForEfficiency = v => {
    if (v === null || v === undefined) return '#e5e7eb';
    // Map 0 -> red, 85 -> orange, 100 -> green
    if (v >= 95) return '#10B981';
    if (v >= 90) return '#34D399';
    if (v >= 85) return '#F59E0B';
    if (v >= 70) return '#FB923C';
    return '#EF4444';
  };
  return <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Inverter Efficiency (%)</CardTitle>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? <Skeleton className="h-96 w-full" /> : <div ref={chartRef} className="w-full h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{
            top: 10,
            right: 20,
            bottom: 80,
            left: 20
          }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="inverter" angle={-90} textAnchor="end" interval={0} height={70} tick={{
              fontSize: 10
            }} />
                <YAxis domain={[0, maxEff]} tick={{
              fontSize: 12
            }} label={{
              value: 'Efficiency (%)',
              angle: -90,
              position: 'insideLeft'
            }} />
                <Tooltip formatter={(value, _name, props) => [`${Number(value).toFixed(2)}%`, props.payload.inverter]} />
                <Legend />
                <Bar dataKey="efficiency" name="Efficiency (%)">
                  {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={colorForEfficiency(entry.efficiency)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>}
      </CardContent>
    </Card>;
};
export default InverterEfficiencyHeatmap;